/*!
 * Dalafisk – recipes
 * ------------------------------------------------------------------
 * Three behaviours, all driven by the same metaobject list the sections render
 * (see dalafisk-recipe.liquid and snippets/dalafisk-recipe-basket.liquid):
 *
 *   1. The portion stepper rescales every ingredient amount.       (recipe page)
 *   2. The buy box totals the ticked items, in the size each one
 *      is set to, and adds them to the cart in one request.       (recipe page)
 *   3. "Lägg till alla" on a recipe CARD adds that recipe's whole
 *      basket in one request.                         (product page, index, …)
 *
 * 2 and 3 are the same trip to the cart with a different set of ids, so they
 * share `addToCart` below rather than each carrying a copy of Pinnacle's
 * section-rendering dance. 3 is delegated from the document, so it works for
 * every card on the page and for cards that arrive later.
 *
 * Everything degrades to nothing: with no amounts there is no stepper, with no
 * linked products there is no buy box and no card footer, and if this file
 * fails to load the pages are still complete — every card links to its recipe
 * and every recipe can be bought from the recipe page's own form.
 */
(function () {
  'use strict';

  if (window.__dalafiskRecipe) return;
  window.__dalafiskRecipe = true;

  /* The three words the controls say while they work. They are here rather
     than in section settings because they are states of one script, not copy a
     merchant tunes per section; the resting labels ARE settings. */
  var BUSY_TEXT = 'Lägger till…';
  var FAIL_TEXT = 'Försök igen';
  var RESTORE_AFTER = 2000;

  /* ---------- helpers ---------- */

  /* Shopify prices are in minor units, and the shop's own money format is the
     only right way to print them: this hand-rolled fallback said "1039,60 kr"
     for a price the Liquid `| money` filter — and therefore every card, price
     and cart line on the site — prints as "1,040 kr". The format travels from
     Liquid on the recipe root, and Pinnacle's vendor.js already carries the
     formatter that reads it. The fallback stays for the case where either is
     missing; it is better than a raw number of öre. */
  var moneyFormat = null;
  function money(cents) {
    if (moneyFormat === null) {
      var host = document.querySelector('[data-dalafisk-recipe]');
      moneyFormat = (host && host.getAttribute('data-money-format')) || '';
    }
    if (moneyFormat && typeof window.formatMoney === 'function') {
      try { return window.formatMoney(cents, moneyFormat); } catch (e) { /* fall through */ }
    }
    var amount = (cents / 100);
    var s = amount.toFixed(amount % 1 === 0 ? 0 : 2).replace('.', ',');
    return s + ' kr';
  }

  // Scaled amounts have to stay readable: 0.5 rather than 0.5000000001, and
  // no "1.0". Quarters are kept because halving 1 dl twice is a real case.
  function tidy(n) {
    var r = Math.round(n * 100) / 100;
    if (Math.abs(r - Math.round(r)) < 0.01) return String(Math.round(r));
    return String(r).replace('.', ',');
  }

  /* ---------- the cart ----------
     Mirrors what Pinnacle's own product card does (app.js: quickAdd) so the
     cart drawer, bubble and cart page all refresh the same way — the only
     difference is that this posts several lines at once via /cart/add.js.  */

  var SECTIONS = [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-count' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-secondary-area--item-amount' }
  ];

  function sectionInner(html, selector) {
    try {
      return new DOMParser().parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    } catch (e) { return null; }
  }

  function renderCart(state) {
    if (!state || !state.sections) return;
    SECTIONS.forEach(function (s) {
      var host = document.getElementById(s.id);
      if (!host || !state.sections[s.section]) return;
      var target = host.querySelector(s.selector) || host;
      var html = sectionInner(state.sections[s.section], s.selector);
      if (html !== null) target.innerHTML = html;
    });
    var drawer = document.getElementById('Cart-Drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      if (typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
    }
  }

  /* One request, N lines. `btn` and `label` are only for feedback — the caller
     decides which ids go, this decides nothing about them. */
  function addToCart(ids, btn, label) {
    if (!ids.length || (btn && btn.disabled)) return;

    var resting = label ? label.textContent : '';
    if (btn) {
      btn.disabled = true;
      btn.classList.add('loading');
    }
    if (label) label.textContent = BUSY_TEXT;

    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart_add_url)
      ? window.theme.routes.cart_add_url
      : '/cart/add';

    return fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }),
        sections: SECTIONS.map(function (s) { return s.section; }).join(','),
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (state && state.status) throw new Error(state.description || state.message);
        renderCart(state);
        if (typeof window.dispatchCustomEvent === 'function') {
          window.dispatchCustomEvent('cart:item-added', {
            product: state && state.items ? state.items[0] : state
          });
        } else {
          document.dispatchEvent(new CustomEvent('cart:item-added', {
            detail: { product: state && state.items ? state.items[0] : state }
          }));
        }
      })
      .catch(function (e) {
        console.error('[dalafisk-recipe] could not add to cart', e);
        // Leave the button usable so the visitor can try again rather than
        // being stuck on a dead control.
        if (label) label.textContent = FAIL_TEXT;
      })
      .finally(function () {
        if (btn) {
          btn.classList.remove('loading');
          btn.disabled = false;
        }
        setTimeout(function () {
          if (label) label.textContent = resting;
        }, RESTORE_AFTER);
      });
  }

  /* ---------- 3. "Lägg till alla" on a card ----------
     Delegated from the document: recipe cards appear on the product page, the
     collection pages, the recipe index and inside a recipe, and no section owns
     all of them. The ids were resolved in Liquid, so the button carries exactly
     the variants the card priced. */
  document.addEventListener('click', function (e) {
    var btn = e.target instanceof Element
      ? e.target.closest('[data-dalafisk-recipe-add]')
      : null;
    if (!btn) return;

    // The footer sits next to the card's link overlay, so a press here is the
    // button's own action and never navigation.
    e.preventDefault();

    var ids = (btn.getAttribute('data-dalafisk-recipe-add') || '')
      .split(',')
      .filter(function (id) { return id !== ''; });
    addToCart(ids, btn, btn.querySelector('[data-dalafisk-recipe-add-label]'));
  });

  /* ---------- the recipe page ---------- */

  var root = document.querySelector('[data-dalafisk-recipe]');
  if (!root) return;

  /* ---------- 1. portions ---------- */

  var base = parseFloat(root.getAttribute('data-base-servings')) || 4;
  var servings = base;
  // The word after the number is a section setting, handed over on the root so
  // it is not spelled out in two places. Blank is allowed: the number alone.
  var servingsUnit = (root.getAttribute('data-servings-unit') || '').trim();
  function servingsText(n) {
    return servingsUnit ? n + ' ' + servingsUnit : String(n);
  }
  var servingsOut = root.querySelector('[data-recipe-servings]');
  var servingsLabel = root.querySelector('[data-recipe-servings-label]');
  var amounts = [].slice.call(root.querySelectorAll('[data-recipe-amount]'));

  function renderPortions() {
    var factor = servings / base;
    amounts.forEach(function (el) {
      var a = parseFloat(el.getAttribute('data-recipe-amount'));
      if (isNaN(a)) return;
      var unit = el.getAttribute('data-recipe-unit') || '';
      el.textContent = tidy(a * factor) + (unit ? ' ' + unit : '');
    });
    if (servingsOut) servingsOut.textContent = servingsText(servings);
    if (servingsLabel) servingsLabel.textContent = servingsText(servings);
    root.querySelectorAll('[data-recipe-step]').forEach(function (b) {
      var dir = parseInt(b.getAttribute('data-recipe-step'), 10);
      // One portion is the floor; there is no sensible "0 servings".
      b.disabled = (dir < 0 && servings <= 1);
    });
  }

  root.querySelectorAll('[data-recipe-step]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var dir = parseInt(btn.getAttribute('data-recipe-step'), 10);
      servings = Math.max(1, servings + dir);
      renderPortions();
    });
  });
  if (amounts.length || servingsOut) renderPortions();

  /* ---------- steps can be ticked off while cooking ----------
     Above the buy box on purpose: a recipe with no linked products has no buy
     box, and when this sat below an early `return` for that case its steps
     could not be ticked — which is exactly the plain-prose recipe where the
     cook has nothing else to interact with. */
  root.querySelectorAll('[data-recipe-step-item]').forEach(function (li) {
    li.addEventListener('click', function () {
      li.setAttribute('data-done', li.getAttribute('data-done') === '1' ? '0' : '1');
    });
  });

  /* ---------- 2. the buy box ---------- */

  var buy = root.querySelector('[data-recipe-buy]');
  if (!buy) return;

  var items = [].slice.call(buy.querySelectorAll('[data-recipe-item]'));
  var countOut = buy.querySelector('[data-recipe-count]');
  var totalOut = buy.querySelector('[data-recipe-total]');
  var addBtn = buy.querySelector('[data-recipe-add]');
  var addLabel = buy.querySelector('[data-recipe-add-label]');

  function chosen() {
    return items.filter(function (i) { return i.checked && !i.disabled; });
  }

  function renderTotals() {
    var picked = chosen();
    var sum = picked.reduce(function (n, i) {
      return n + (parseInt(i.getAttribute('data-price'), 10) || 0);
    }, 0);
    if (countOut) {
      countOut.textContent = picked.length === 1
        ? '1 vara'
        : picked.length + ' varor';
    }
    if (totalOut) totalOut.textContent = money(sum);
    if (addBtn) addBtn.disabled = picked.length === 0;
  }

  /* A product with several variants has a size menu under its name. The
     menu never talks to the totals or the cart itself: it copies the chosen
     variant's id and price onto the row's checkbox, which is all `chosen()`,
     `renderTotals()` and the add button ever read. */
  function applySize(select, byVisitor) {
    var row = select.closest('.dalafisk-recipe__item');
    var box = row && row.querySelector('[data-recipe-item]');
    var opt = select.options[select.selectedIndex];
    if (!box || !opt) return;
    // On load the markup already describes its own default. Only a size the
    // browser restored on Back needs copying over — and rewriting the price
    // anyway would swap Liquid's `| money` for the script's formatter.
    if (!byVisitor && opt.defaultSelected) return;
    var price = parseInt(opt.getAttribute('data-price'), 10) || 0;
    box.setAttribute('data-variant-id', opt.value);
    box.setAttribute('data-price', String(price));
    var priceOut = row.querySelector('[data-recipe-item-price]');
    if (priceOut) priceOut.textContent = money(price);
    // Choosing a size on a row that was ticked off means "I want this after all".
    if (byVisitor && !box.disabled) box.checked = true;
  }

  buy.querySelectorAll('[data-recipe-variant]').forEach(function (select) {
    applySize(select, false);
    select.addEventListener('change', function () {
      applySize(select, true);
      renderTotals();
    });
  });

  items.forEach(function (i) { i.addEventListener('change', renderTotals); });
  renderTotals();

  if (addBtn) {
    addBtn.addEventListener('click', function () {
      var ids = chosen().map(function (i) { return i.getAttribute('data-variant-id'); });
      var done = addToCart(ids, addBtn, addLabel);
      // The box's own button owes one more thing than a card's: the ticked set
      // decides whether it may be pressed again at all.
      if (done && done.then) done.then(renderTotals);
    });
  }
})();
